import React, { useEffect, useMemo, useState } from 'react';
import type {
  CreateSupplierPayload,
  Supplier,
  SupplierPixKeyType,
  SupplierPortalUpdatePayload,
} from '../api';
import { useEstados, useMunicipios } from '../../geo/hooks';
import { BRAZILIAN_BANKS, bankByCode } from '../constants/banks';
import {
  maskBrazilianPhone,
  maskCEP,
  maskPixKey,
  normalizeSupplierPortalPayload,
  shouldUseCustomBankFields,
  supplierToPortalForm,
  type SupplierFormErrors,
  validateSupplierPortalForm,
} from '../validation/supplierForm';

interface SupplierPortalRegistrationFormProps {
  supplier: Supplier;
  submitting: boolean;
  onSubmit: (payload: SupplierPortalUpdatePayload) => Promise<void>;
}

const parseList = (value: string) => value
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);

export function SupplierPortalRegistrationForm({
  supplier,
  submitting,
  onSubmit,
}: SupplierPortalRegistrationFormProps) {
  const [form, setForm] = useState<CreateSupplierPayload>(() => supplierToPortalForm(supplier));
  const [errors, setErrors] = useState<SupplierFormErrors>({});
  const [categoriesText, setCategoriesText] = useState(() => supplier.categories.join(', '));
  const [servicesText, setServicesText] = useState(() => supplier.services.join(', '));
  const [customBank, setCustomBank] = useState(() => (
    shouldUseCustomBankFields(supplier.bankCode, supplier.bankName)
  ));
  const estadosQuery = useEstados();
  const municipiosQuery = useMunicipios(form.addressState);
  const municipalities = useMemo(
    () => (municipiosQuery.data ?? []).slice().sort((left, right) => (
      left.nome.localeCompare(right.nome, 'pt-BR')
    )),
    [municipiosQuery.data],
  );

  useEffect(() => {
    const nextForm = supplierToPortalForm(supplier);
    setForm(nextForm);
    setCategoriesText(supplier.categories.join(', '));
    setServicesText(supplier.services.join(', '));
    setCustomBank(shouldUseCustomBankFields(nextForm.bankCode, nextForm.bankName));
    setErrors({});
  }, [supplier.id, supplier.updatedAt]);

  const clearError = (field: keyof CreateSupplierPayload) => {
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const setField = <K extends keyof CreateSupplierPayload>(
    field: K,
    value: CreateSupplierPayload[K],
  ) => {
    setForm((current) => ({ ...current, [field]: value }));
    clearError(field);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const candidate = {
      ...form,
      categories: parseList(categoriesText),
      services: parseList(servicesText),
    };
    const validationErrors = validateSupplierPortalForm(candidate);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      const firstInvalidField = Object.keys(validationErrors)[0];
      requestAnimationFrame(() => {
        document.querySelector<HTMLElement>(`[data-portal-field="${firstInvalidField}"]`)?.focus();
      });
      return;
    }

    try {
      await onSubmit(normalizeSupplierPortalPayload(candidate));
    } catch {
      // A mensagem da API é apresentada pelo contêiner do portal.
    }
  };

  const selectedCityExists = municipalities.some((city) => city.nome === form.addressCity);

  return (
    <form
      noValidate
      onSubmit={handleSubmit}
      aria-labelledby="supplier-portal-registration-title"
      className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
    >
      <div className="mb-5">
        <h2 id="supplier-portal-registration-title" className="font-semibold text-gray-900">
          Atualização cadastral
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Identificação legal, status, risco e homologação são protegidos e não podem ser alterados pelo portal.
        </p>
      </div>

      <fieldset>
        <legend className="text-sm font-semibold text-gray-900">Identificação e contato</legend>
        <div className="mt-3 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <ReadonlyField label={supplier.supplierType === 'pessoa_juridica' ? 'CNPJ' : 'CPF'} value={supplier.documentNumber} />
          <ReadonlyField label="Razão Social / Nome" value={supplier.corporateName} />
          <PortalInput field="tradeName" label="Nome Fantasia" value={form.tradeName ?? ''} onChange={(value) => setField('tradeName', value)} error={errors.tradeName} maxLength={180} />
          <PortalInput field="contactName" label="Contato" value={form.contactName} onChange={(value) => setField('contactName', value)} error={errors.contactName} maxLength={100} required />
          <PortalInput field="contactEmail" label="E-mail" type="email" value={form.contactEmail} onChange={(value) => setField('contactEmail', value)} error={errors.contactEmail} maxLength={254} required />
          <PortalInput field="contactPhone" label="Telefone" value={form.contactPhone} onChange={(value) => setField('contactPhone', maskBrazilianPhone(value))} error={errors.contactPhone} maxLength={15} inputMode="tel" required />
          <PortalInput field="stateRegistration" label="Inscrição Estadual" value={form.stateRegistration ?? ''} onChange={(value) => setField('stateRegistration', value)} error={errors.stateRegistration} maxLength={30} />
          <PortalInput field="municipalRegistration" label="Inscrição Municipal" value={form.municipalRegistration ?? ''} onChange={(value) => setField('municipalRegistration', value)} error={errors.municipalRegistration} maxLength={30} />
          <PortalInput field="taxRegime" label="Regime Fiscal" value={form.taxRegime ?? ''} onChange={(value) => setField('taxRegime', value)} error={errors.taxRegime} maxLength={60} />
        </div>
      </fieldset>

      <fieldset className="mt-6 border-t border-gray-200 pt-5">
        <legend className="px-2 text-sm font-semibold text-gray-900">Dados bancários</legend>
        <div className="mt-3 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <PortalSelect
            field="bankCode"
            controlId="supplier-portal-bank-selection"
            label="Banco"
            value={customBank ? '__custom__' : form.bankCode ?? ''}
            error={customBank ? undefined : errors.bankCode}
            onChange={(value) => {
              if (value === '__custom__') {
                setCustomBank(true);
                setField('bankCode', '');
                setField('bankName', '');
                return;
              }
              const bank = bankByCode(value);
              setCustomBank(false);
              setField('bankCode', value || undefined);
              setField('bankName', bank?.name || undefined);
            }}
          >
            <option value="">Selecione pelo código COMPE</option>
            {BRAZILIAN_BANKS.map((bank) => (
              <option key={bank.code} value={bank.code}>{bank.code} – {bank.name}</option>
            ))}
            <option value="__custom__">Outro código COMPE</option>
          </PortalSelect>
          {customBank && (
            <>
              <PortalInput field="bankCode" label="Código COMPE" value={form.bankCode ?? ''} onChange={(value) => setField('bankCode', value.replace(/\D/g, '').slice(0, 3))} error={errors.bankCode} maxLength={3} inputMode="numeric" />
              <PortalInput field="bankName" label="Instituição bancária" value={form.bankName ?? ''} onChange={(value) => setField('bankName', value)} error={errors.bankName} maxLength={120} />
            </>
          )}
          <PortalInput field="bankAgency" label="Agência" value={form.bankAgency ?? ''} onChange={(value) => setField('bankAgency', value.toUpperCase().replace(/[^0-9A-Z-]/g, '').slice(0, 20))} error={errors.bankAgency} maxLength={20} />
          <PortalInput field="bankAccount" label="Conta" value={form.bankAccount ?? ''} onChange={(value) => setField('bankAccount', value.toUpperCase().replace(/[^0-9A-Z-]/g, '').slice(0, 30))} error={errors.bankAccount} maxLength={30} />
          <PortalSelect field="bankAccountType" label="Tipo de conta" value={form.bankAccountType ?? ''} error={errors.bankAccountType} onChange={(value) => setField('bankAccountType', value || undefined)}>
            <option value="">Selecione</option>
            <option value="corrente">Conta corrente</option>
            <option value="poupanca">Conta poupança</option>
            <option value="pagamento">Conta de pagamento</option>
          </PortalSelect>
          <PortalSelect
            field="bankPixKeyType"
            label="Tipo de chave PIX"
            value={form.bankPixKeyType ?? ''}
            error={errors.bankPixKeyType}
            onChange={(value) => {
              setField('bankPixKeyType', (value || undefined) as SupplierPixKeyType | undefined);
              setField('bankPixKey', '');
            }}
          >
            <option value="">Selecione</option>
            <option value="cpf">CPF</option>
            <option value="cnpj">CNPJ</option>
            <option value="email">E-mail</option>
            <option value="phone">Telefone</option>
            <option value="random">Chave aleatória</option>
          </PortalSelect>
          <PortalInput field="bankPixKey" label="Chave PIX" value={form.bankPixKey ?? ''} onChange={(value) => setField('bankPixKey', maskPixKey(value, form.bankPixKeyType))} error={errors.bankPixKey} maxLength={form.bankPixKeyType === 'email' ? 254 : form.bankPixKeyType === 'cnpj' ? 18 : form.bankPixKeyType === 'cpf' ? 14 : form.bankPixKeyType === 'phone' ? 15 : 36} disabled={!form.bankPixKeyType} />
        </div>
      </fieldset>

      <fieldset className="mt-6 border-t border-gray-200 pt-5">
        <legend className="px-2 text-sm font-semibold text-gray-900">Endereço e fornecimento</legend>
        <div className="mt-3 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <PortalInput field="addressZipCode" label="CEP" value={form.addressZipCode ?? ''} onChange={(value) => setField('addressZipCode', maskCEP(value))} error={errors.addressZipCode} maxLength={9} inputMode="numeric" />
          <PortalInput field="addressStreet" label="Logradouro" value={form.addressStreet ?? ''} onChange={(value) => setField('addressStreet', value)} error={errors.addressStreet} maxLength={180} />
          <PortalInput field="addressNumber" label="Número" value={form.addressNumber ?? ''} onChange={(value) => setField('addressNumber', value)} error={errors.addressNumber} maxLength={20} placeholder="Ex.: 123-A ou S/N" />
          <PortalInput field="addressComplement" label="Complemento" value={form.addressComplement ?? ''} onChange={(value) => setField('addressComplement', value)} error={errors.addressComplement} maxLength={120} />
          <PortalInput field="addressDistrict" label="Bairro" value={form.addressDistrict ?? ''} onChange={(value) => setField('addressDistrict', value)} error={errors.addressDistrict} maxLength={100} />
          <PortalSelect
            field="addressState"
            label="UF"
            value={form.addressState ?? ''}
            error={errors.addressState}
            disabled={estadosQuery.isLoading}
            onChange={(value) => {
              setField('addressState', value || undefined);
              setField('addressCity', '');
            }}
          >
            <option value="">Selecione a UF</option>
            {(estadosQuery.data ?? []).map((state) => (
              <option key={state.uf} value={state.uf}>{state.uf} – {state.nome}</option>
            ))}
          </PortalSelect>
          {municipiosQuery.isError ? (
            <PortalInput field="addressCity" label="Cidade" value={form.addressCity ?? ''} onChange={(value) => setField('addressCity', value)} error={errors.addressCity} maxLength={100} />
          ) : (
            <PortalSelect field="addressCity" label="Cidade" value={form.addressCity ?? ''} error={errors.addressCity} disabled={!form.addressState || municipiosQuery.isLoading} onChange={(value) => setField('addressCity', value || undefined)}>
              <option value="">{form.addressState ? 'Selecione a cidade' : 'Selecione primeiro a UF'}</option>
              {form.addressCity && !selectedCityExists && <option value={form.addressCity}>{form.addressCity}</option>}
              {municipalities.map((city) => <option key={city.idIbge} value={city.nome}>{city.nome}</option>)}
            </PortalSelect>
          )}
          <PortalInput field="addressCountry" label="País" value={form.addressCountry ?? ''} onChange={(value) => setField('addressCountry', value)} error={errors.addressCountry} maxLength={60} />
          <PortalInput field="categories" label="Categorias" value={categoriesText} onChange={(value) => {
            setCategoriesText(value);
            clearError('categories');
          }} error={errors.categories} maxLength={500} />
          <PortalInput field="services" label="Serviços prestados" value={servicesText} onChange={(value) => {
            setServicesText(value);
            clearError('services');
          }} error={errors.services} maxLength={1000} />
        </div>
      </fieldset>

      <div className="mt-5 flex justify-end">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {submitting ? 'Salvando...' : 'Salvar atualização cadastral'}
        </button>
      </div>
    </form>
  );
}

function PortalInput({
  field,
  label,
  error,
  onChange,
  ...inputProps
}: {
  field: keyof CreateSupplierPayload;
  label: string;
  error?: string;
  onChange: (value: string) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const id = `supplier-portal-${field}`;
  const errorId = `${id}-error`;
  return (
    <label htmlFor={id} className="block text-sm text-gray-700">
      <span className="mb-1 block font-medium">{label}{inputProps.required ? ' *' : ''}</span>
      <input
        {...inputProps}
        id={id}
        data-portal-field={field}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${error ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'}`}
      />
      {error && <span id={errorId} role="alert" className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}

function PortalSelect({
  field,
  controlId,
  label,
  value,
  error,
  onChange,
  children,
  disabled,
}: {
  field: keyof CreateSupplierPayload;
  controlId?: string;
  label: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  const id = controlId ?? `supplier-portal-${field}`;
  const errorId = `${id}-error`;
  return (
    <label htmlFor={id} className="block text-sm text-gray-700">
      <span className="mb-1 block font-medium">{label}</span>
      <select
        id={id}
        data-portal-field={field}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 disabled:bg-gray-100 ${error ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'}`}
      >
        {children}
      </select>
      {error && <span id={errorId} role="alert" className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}

function ReadonlyField({ label, value }: { label: string; value: string }) {
  const id = `supplier-portal-readonly-${label.toLocaleLowerCase('pt-BR').replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <label htmlFor={id} className="block text-sm text-gray-700">
      <span className="mb-1 block font-medium">{label}</span>
      <input id={id} value={value} readOnly aria-readonly="true" className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600" />
    </label>
  );
}
